import { MonotonicClock, type ClockQuality } from "@infoflyt/monotonic-clock";
import type { ClockReading } from "@infoflyt/shared/timecode";

/**
 * Broen mellom den asynkrone plugin-en og den synkrone avlesningen appen
 * trenger.
 *
 * Tidskoden fanges ved knappetrykk, ikke ved lagring — og et trykk kan ikke
 * vente på en promise. Løsningen er at begge klokkene er monotone innenfor
 * samme oppstart, så forskjellen mellom dem er konstant. Vi måler den én
 * gang, og etter det er en synkron avlesning eksakt.
 *
 * Se docs/04-timecode.md.
 */

interface Calibration {
  /** elapsedRealtimeMs − performance.now() i samme øyeblikk. */
  readonly offset: number;
  readonly bootId: string;
  readonly quality: ClockQuality;
}

let calibration: Calibration | null = null;

/** Største aksepterte avvik mellom to kalibreringer før noe er galt. */
const RECALIBRATE_TOLERANCE_MS = 250;

/** Maaler forskyvningen uten aa ta den i bruk. */
async function measure(): Promise<Calibration> {
  // Les performance.now() på begge sider av plugin-kallet, slik at
  // rundturen ikke havner i forskyvningen.
  const before = performance.now();
  const [elapsed, boot] = await Promise.all([
    MonotonicClock.elapsedRealtimeMs(),
    MonotonicClock.bootId(),
  ]);
  const after = performance.now();
  const mid = before + (after - before) / 2;

  return {
    offset: elapsed.value - mid,
    bootId: boot.value,
    quality: elapsed.quality,
  };
}

/**
 * Setter forskyvningen. Gjoeres én gang per oppstart.
 */
export async function calibrateClock(): Promise<Calibration> {
  const next = await measure();
  calibration = next;
  return next;
}

/**
 * Synkron avlesning av begge klokkene i samme øyeblikk.
 *
 * `wallUtcMs` brukes bare til sortering, visning og til å regne fram et nytt
 * anker over en omstart. Forløpt tid kommer alltid fra `monotonicMs`.
 */
export function readClock(): ClockReading {
  const wallUtcMs = Date.now();
  if (calibration === null) {
    // Før første kalibrering finnes bare sideøktens egen telling. Ankeret
    // opprettes uansett ikke før kalibreringen er inne.
    return { monotonicMs: performance.now(), bootId: "uncalibrated", wallUtcMs };
  }
  return {
    monotonicMs: performance.now() + calibration.offset,
    bootId: calibration.bootId,
    wallUtcMs,
  };
}

export function clockQuality(): ClockQuality {
  return calibration?.quality ?? "wall";
}

/**
 * Måler på nytt etter at appen har vært borte.
 *
 * MÅLER, og setter bare når tellingen faktisk har begynt på nytt — altså
 * når `bootId` er en annen, eller avviket er så stort at den gamle
 * forskyvningen ikke kan stemme.
 *
 * Dette er hele grunnen til at appen går som en boks. Satte vi forskyvningen
 * hver gang, ville den bli utledet av `Date.now()` på nytt ved hver eneste
 * fremhenting: en justering av veggklokka på under toleransen ble da skrevet
 * rett inn i `monotonicMs` uten at noe ble merket usikkert, og den summerte
 * seg over et skift. Det er å re-synkronisere underveis, og det skal ikke
 * skje. Se CLAUDE.md og docs/04-timecode.md.
 *
 * Det ødela også målingen: `clockSkewMs` ville bare sett driften siden
 * forrige fremhenting, og appen hentes fram hele tiden. Drifttesten i fase 1
 * ville rapportert nær null uansett hvor mye klokka faktisk drev, og gitt en
 * falsk godkjenning. Se docs/10-slagplan.md.
 */
export async function recalibrate(): Promise<{ changed: boolean; deltaMs: number }> {
  const previous = calibration;
  const next = await measure();
  if (previous === null) {
    calibration = next;
    return { changed: true, deltaMs: 0 };
  }
  const deltaMs = next.offset - previous.offset;
  const changed = previous.bootId !== next.bootId
    || Math.abs(deltaMs) > RECALIBRATE_TOLERANCE_MS;
  // Bare en ny oppstart gir ny forskyvning. Ellers beholdes den vi har.
  if (changed) calibration = next;
  return { changed, deltaMs };
}

export async function deviceIsSecure(): Promise<boolean> {
  const { value } = await MonotonicClock.isDeviceSecure();
  return value;
}
